import { createDisabledCommercePort, platformCapabilities, type CapabilitySet, type Channel, type CommercePort } from '@wolhasudang/shared';
import type { VerificationFeedbackVerdict } from './verification-feedback.js';

export interface PlatformPort {
  channel: Channel;
  capabilities: CapabilitySet;
  commerce: CommercePort;
  apiUrl(path: string): string;
  share(data: ShareData): Promise<'shared' | 'copied' | 'unsupported'>;
  haptic(verdict: VerificationFeedbackVerdict): Promise<'played' | 'unsupported'>;
}

const productionApiOrigin = 'https://wolhasudang.com';
const clipboardFallbackMaximumWaitMs = 1_500;

const webPlatform: PlatformPort = {
  channel: 'web', capabilities: platformCapabilities.web, commerce: createDisabledCommercePort('web'),
  apiUrl: (path) => path,
  async share(data) {
    if (navigator.share) {
      const shareable = !data.files?.length || !navigator.canShare || navigator.canShare(data);
      if (shareable) { await navigator.share(data); return 'shared'; }
      const withoutFiles: ShareData = { ...(data.title ? { title: data.title } : {}), ...(data.text ? { text: data.text } : {}), ...(data.url ? { url: data.url } : {}) };
      await navigator.share(withoutFiles); return 'shared';
    }
    if (data.text && navigator.clipboard) {
      return await Promise.race([
        navigator.clipboard.writeText(data.text).then(() => 'copied' as const).catch(() => 'unsupported' as const),
        new Promise<'unsupported'>((resolve) => globalThis.setTimeout(() => resolve('unsupported'), clipboardFallbackMaximumWaitMs)),
      ]);
    }
    return 'unsupported';
  },
  async haptic(verdict) {
    if (!navigator.vibrate) return 'unsupported';
    return navigator.vibrate(verdict === 'match' ? [20, 28, 20] : 32) ? 'played' : 'unsupported';
  },
};

const tossPlatform: PlatformPort = {
  channel: 'toss', capabilities: platformCapabilities.toss, commerce: createDisabledCommercePort('toss'),
  apiUrl: (path) => new URL(path, productionApiOrigin).toString(),
  async share(data) {
    const message = [data.title, data.text, data.url].filter(Boolean).join('\n');
    if (!message) return 'unsupported';
    const { share } = await import('@apps-in-toss/web-framework');
    await share({ message });
    return 'shared';
  },
  async haptic(verdict) {
    const { generateHapticFeedback } = await import('@apps-in-toss/web-framework');
    await generateHapticFeedback({ type: verdict === 'match' ? 'success' : 'softMedium' });
    return 'played';
  },
};

export const platform: PlatformPort = __WOLHASUDANG_CHANNEL__ === 'toss' ? tossPlatform : webPlatform;
